import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, message, Progress, Icon } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { BULK_UNPUBLISH_PROPERTIES } from './queries';
import { CloseOutlined } from '@ant-design/icons';

const PropertiesBulkOperationStatus = {
  Processing: 'processing',
  ManualConfirm: 'manualConfirm',
  Glitch: 'glitch',
  Failed: 'failed',
  Success: 'success',
};

function PropertiesBulkOperationModal({
  selectedProperties,
  bulkOperation,
  onClose,
  onSuccess,
  unpublishReason,
}) {
  const { t } = useTranslation();
  const timerId = useRef();
  const [processBarPercent, setProcessBarPercent] = useState(0);
  const [currentSelectedProperties, setCurrentSelectedProperties] = useState(selectedProperties);
  const [status, setStatus] = useState(PropertiesBulkOperationStatus.Processing);
  const [invalidUnpublishedProperties, setInvalidUnpublishedProperties] = useState([]);

  const [bulkUnpublishProperties, { loading: bulkUnpublishPropertiesLoading }] = useMutation(
    BULK_UNPUBLISH_PROPERTIES,
    {
      onError: e => {
        message.error(e.message);
        stopProcessBar();
        setStatus(PropertiesBulkOperationStatus.Failed);
      },
    },
  );

  const handleSubmit = useCallback(
    async (bulkOperationWay, skipConfirm, selectedPropertyIds, skipPropertyIds) => {
      setStatus(PropertiesBulkOperationStatus.Processing);
      startProcessBar();
      if (bulkOperationWay === 'unpublish') {
        const propertyIds = selectedPropertyIds.filter(
          selectedId => !skipPropertyIds.includes(selectedId),
        );
        setCurrentSelectedProperties(propertyIds);
        if (skipConfirm && propertyIds.length === 0) {
          return setStatus(PropertiesBulkOperationStatus.Glitch);
        }

        const response = await bulkUnpublishProperties({
          variables: {
            input: {
              propertyIds: propertyIds,
              unpublishReason: unpublishReason,
              skipVerifyProperty: skipConfirm,
            },
          },
        });
        if (
          response?.data?.bulkUnpublishProperties?.status === 'SUCCESS' &&
          response?.data?.bulkUnpublishProperties?.invalidUnpublishedProperties.length === 0
        ) {
          setStatus(PropertiesBulkOperationStatus.Success);
          onSuccess();
        }
        if (response?.data?.bulkUnpublishProperties?.invalidUnpublishedProperties.length) {
          setInvalidUnpublishedProperties(
            response?.data?.bulkUnpublishProperties?.invalidUnpublishedProperties,
          );
          if (skipConfirm) {
            setStatus(PropertiesBulkOperationStatus.Glitch);
          } else {
            setStatus(PropertiesBulkOperationStatus.ManualConfirm);
          }
        }
        if (response?.data?.bulkUnpublishProperties?.error) {
          setStatus(PropertiesBulkOperationStatus.Failed);
        }
      }
    },
    [],
  );

  const stopProcessBar = () => {
    clearInterval(timerId.current);
    setProcessBarPercent(0);
  };

  const calcProcessBar = () => {
    setProcessBarPercent(current => (current + 1 < 99 ? current + 1 : current));
  };

  const startProcessBar = () => {
    const intervalID = window.setInterval(calcProcessBar, 100);
    timerId.current = intervalID;
  };

  useEffect(() => {
    handleSubmit(bulkOperation, false, currentSelectedProperties, []);
  }, []);

  useEffect(() => {
    if (status !== PropertiesBulkOperationStatus.Processing) {
      stopProcessBar();
    }
  }, [status]);

  useEffect(() => {
    if (processBarPercent >= 99) {
      stopProcessBar();
    }
  }, [processBarPercent]);

  return (
    <div className="properties-bulk-operation-container">
      <div className="properties-bulk-operation-container__header">
        <Choose>
          <When condition={status === PropertiesBulkOperationStatus.Processing}>
            {t('cms.properties_bulk_operation.modal.title.processing')}
          </When>
          <When condition={status === PropertiesBulkOperationStatus.ManualConfirm}>
            {t('cms.properties_bulk_operation.modal.title.need_confirm')}
          </When>
          <Otherwise>{t('cms.properties_bulk_operation.modal.title.result')}</Otherwise>
        </Choose>
        <CloseOutlined
          className="properties-bulk-operation-container__close-button"
          onClick={onClose}
        />
      </div>
      <div className="properties-bulk-operation-container__body">
        <section className="properties-bulk-operation-container__section">
          <Choose>
            <When condition={status === PropertiesBulkOperationStatus.Processing}>
              <Progress percent={processBarPercent} showInfo />
            </When>
            <When condition={status === PropertiesBulkOperationStatus.ManualConfirm}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="warning"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--warning"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {t('cms.properties_bulk_operation.tips.unpublish.confirm_skip')}
                </p>
              </div>
              <FailedProperties properties={invalidUnpublishedProperties} showTips />
            </When>
            <When condition={status === PropertiesBulkOperationStatus.Glitch}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="warning"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--warning"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {t('cms.properties_bulk_operation.tips.unpublish.glitch')}
                </p>
              </div>
              <FailedProperties properties={invalidUnpublishedProperties} showTips />
            </When>
            <When condition={status === PropertiesBulkOperationStatus.Failed}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="close-circle"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--danger"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {t('cms.properties_bulk_operation.tips.unpublish.failed')}
                </p>
              </div>
            </When>
            <When condition={status === PropertiesBulkOperationStatus.Success}>
              <div className="properties-bulk-operation-container__tooltip">
                <Icon
                  type="check-circle"
                  theme="filled"
                  className="properties-bulk-operation-container__tooltip__icon properties-bulk-operation-container__tooltip__icon--success"
                />
                <p className="properties-bulk-operation-container__tooltip__title">
                  {t('cms.properties_bulk_operation.tips.unpublish.success')}
                </p>
              </div>
            </When>
          </Choose>
        </section>
      </div>
      {status !== PropertiesBulkOperationStatus.Processing ? (
        <div className="properties-bulk-operation-container__footer">
          {status === PropertiesBulkOperationStatus.ManualConfirm ? (
            <Button
              size="large"
              className="properties-bulk-operation-container__cancel-button"
              onClick={onClose}
            >
              {t('cms.properties_bulk_operation.action.btn.cancel')}
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              className="properties-bulk-operation-container__save-button"
              onClick={onClose}
            >
              {t('cms.properties_bulk_operation.action.btn.got')}
            </Button>
          )}

          <If condition={status === PropertiesBulkOperationStatus.ManualConfirm}>
            <Button
              type="primary"
              size="large"
              className="properties-bulk-operation-container__save-button"
              loading={bulkUnpublishPropertiesLoading}
              onClick={() =>
                handleSubmit(
                  bulkOperation,
                  true,
                  currentSelectedProperties,
                  invalidUnpublishedProperties.map(i => i.id),
                )
              }
            >
              {t('cms.properties_bulk_operation.action.btn.confirm')}
            </Button>
          </If>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

function FailedProperties({ properties, showTips }) {
  const { t } = useTranslation();

  return (
    <div className="properties-bulk-operation-container__failed-properties">
      {showTips ? (
        <p className="properties-bulk-operation-container__failed-properties__tips">
          {t('cms.properties_bulk_operation.tips.unpublish.failed_list')}
        </p>
      ) : null}

      {properties.map(property => (
        <div
          key={property.id}
          className="properties-bulk-operation-container__failed-properties__property"
        >
          <Icon
            type="home"
            className="properties-bulk-operation-container__failed-properties__property__icon"
          />
          <span className="properties-bulk-operation-container__failed-properties__property__label">
            {property.propertyName}
          </span>
        </div>
      ))}
    </div>
  );
}

export default PropertiesBulkOperationModal;
